/**
 * AI Provider Configuration Types
 *
 * Unified configuration for LLM, TTS, and Imagen providers.
 * Provider-centric approach: all settings for a provider in one place.
 *
 * Moved from contexts/admin/types/aiProvider.ts during AI context extraction.
 */

import type { ManifestProviderId } from './config';
import type { VoiceMappingEntry } from './voice';

/**
 * LLM Provider Type — a provider id of the manifest's `llm` task.
 * Open on purpose: the worker manifest is the fleet's only provider list
 * (see ManifestProviderId in ./config).
 */
export type LLMProviderType = ManifestProviderId;

/**
 * Google LLM generation settings
 */
export interface GoogleLLMSettings {
  maxOutputTokens: number;
  temperature: number;
  topP: number;
  topK: number;
}

/**
 * Google Vertex AI (Gemini) LLM configuration
 */
export interface LLMGoogleConfig {
  model: string;       // e.g. 'gemini-3-flash-preview'
  location: string;    // e.g. 'us-central1'
  settings: GoogleLLMSettings;
}

/**
 * LLM Provider: Azure OpenAI or Google Vertex AI (Gemini)
 *
 * Both configs are stored so switching provider doesn't lose settings.
 */
export interface LLMProviderConfig {
  provider: LLMProviderType;

  azure?: {
    deploymentName: string;
    /** Azure OpenAI API version (e.g. '2024-12-01-preview'). Defaults to '2024-12-01-preview' if not set. */
    apiVersion?: string;

    settings: {
      maxTokens: number;
      temperature: number;
      topP: number;
      frequencyPenalty: number;
      presencePenalty: number;
    };

    limits?: {
      maxPromptTokens: number;
      maxContextWindow: number;
    };
  };

  google?: LLMGoogleConfig;
}

/**
 * TTS Provider: ElevenLabs
 */
export interface TTSProviderConfig {
  provider: 'elevenlabs';

  elevenlabs: {
    model: string;
    modelVersion: string;

    settings: {
      stability: number;
      similarityBoost: number;
      style: number;
      useSpeakerBoost: boolean;
    };

    voiceMappings: VoiceMappingEntry[];
  };
}

// ============================================================================
// Imagen Provider Types (Google Vertex AI)
// ============================================================================

export type ImagenAspectRatio = '1:1' | '16:9' | '9:16' | '4:3' | '3:4';

export type SafetyFilterLevel =
  | 'BLOCK_LOW_AND_ABOVE'
  | 'BLOCK_MEDIUM_AND_ABOVE'
  | 'BLOCK_ONLY_HIGH';

export type PersonGeneration = 'allow_adult' | 'allow_all' | 'dont_allow';

export type ImageProviderType = 'imagen' | 'gemini';

/** Output image size. Gemini supports 512/1K/2K/4K, Imagen supports 1K/2K. */
export type ImageOutputSize = '512' | '1K' | '2K' | '4K';

export interface ImagenSettings {
  defaultAspectRatio: ImagenAspectRatio;
  sampleCount: number;
  personGeneration: PersonGeneration;
  safetyFilterLevel: SafetyFilterLevel;
  enhancePrompt: boolean;
  /** Output image resolution. Defaults to '1K' if not set. */
  imageSize?: ImageOutputSize;
}

/** A provider id of the manifest's `image` task (open — see ManifestProviderId). */
export type ImagenProviderType = ManifestProviderId;

export interface ImagenProviderConfig {
  provider: ImagenProviderType;

  google: {
    providerType: ImageProviderType;
    model: string;
    location?: string;
    /** API version for Google AI Studio (e.g. 'v1beta', 'v1alpha'). Defaults to 'v1beta'. */
    apiVersion?: string;
    settings: ImagenSettings;
  };

  /** Azure OpenAI image models (gpt-image-2). Same resource + key as the LLM
   *  provider; only the api-version differs. Present when provider ==='azure_openai'. */
  azure?: {
    model: string;
    /** Azure image api-version, e.g. '2025-04-01-preview'. */
    apiVersion?: string;
  };
}

// ============================================================================
// Complete AI Provider Configuration
// ============================================================================

/**
 * Complete AI Provider Configuration
 *
 * Singleton document containing all AI provider settings.
 * Stored in admin_settings container with id='ai_provider_config'.
 */
export interface AIProviderConfig {
  id: 'ai_provider_config';
  llm: LLMProviderConfig;
  tts: TTSProviderConfig;
  imagen: ImagenProviderConfig;
  updatedAt: string;
  updatedBy: string;
}
